/**
 * #48 도움말: one source for /support and the in-app help screen. Answers
 * describe what the product does today; update them with the feature.
 */
export interface HelpItem {
	id: string;
	topic: "record" | "edit" | "report" | "household" | "data" | "plus";
	q: string;
	a: string;
	action?: { label: string; href: string };
}

export const HELP_ITEMS: HelpItem[] = [
	{
		id: "paste",
		topic: "record",
		q: "결제 알림은 어떻게 붙여 넣나요?",
		a: "문자나 카드 앱 알림을 길게 눌러 복사한 뒤 '새 기록 → 결제 알림 붙여 넣기'를 눌러요. 여러 건을 한 번에 붙여도 빈 줄 기준으로 나눠 읽어요.",
		action: { label: "기록 화면 열기", href: "/app/capture" },
	},
	{
		id: "iphone",
		topic: "record",
		q: "아이폰은 문자를 자동으로 못 읽나요?",
		a: "아이폰은 다른 앱이 문자를 읽지 못하게 막아요. 복사 한 번은 필요하고, 단축어를 만들면 한 번 누르는 것으로 기록 화면까지 열 수 있어요.",
		action: {
			label: "단축어 만드는 법",
			href: "/guide/iphone-auto-entry#shortcut",
		},
	},
	{
		id: "android-share",
		topic: "record",
		q: "안드로이드 문자에서 바로 보낼 수 있나요?",
		a: "소비도감을 홈 화면에 추가하면 문자 앱의 공유 목록에 소비도감이 생겨요. 결제 문자를 공유하면 확인 화면이 바로 열려요.",
		action: { label: "홈 화면에 추가", href: "/app/settings#reminders" },
	},
	{
		id: "photo",
		topic: "record",
		q: "영수증 사진이 읽히지 않아요.",
		a: "사진 자동 읽기는 아직 준비 중이에요. 사진은 서버로 보내지 않고, 사진을 보며 매장과 금액을 바로 넣는 화면으로 이어져요.",
	},
	{
		id: "manual-category",
		topic: "record",
		q: "직접 입력할 때 분류는 어떻게 정해지나요?",
		a: "매장 이름으로 분류를 추정하고, 모르면 비워 둬요. 분류를 고르지 않으면 저장되지 않아서 '기타'로 몰래 쌓이지 않아요.",
	},
	{
		id: "undo",
		topic: "edit",
		q: "방금 저장한 기록을 취소하고 싶어요.",
		a: "저장 직후 아래에 6초 동안 '되돌리기'가 보여요. 지나간 뒤에는 내역에서 지우면 휴지통으로 가고, 휴지통에서 복원할 수 있어요.",
	},
	{
		id: "edit",
		topic: "edit",
		q: "금액이나 매장을 잘못 넣었어요.",
		a: "내역에서 기록을 누르고 '고치기'로 매장·금액·분류·날짜와 시각을 바꿔요. 합계·리포트·한도가 같이 바뀌어요.",
		action: { label: "내역 열기", href: "/app/records" },
	},
	{
		id: "store-rule",
		topic: "edit",
		q: "같은 매장 분류를 매번 바꿔야 하나요?",
		a: "분류를 바꿀 때 '다음에도 이 분류로'를 켜 두면 그 매장의 다음 기록부터 자동으로 들어가요. 규칙은 설정 → 분류와 태그에서 보고 지울 수 있어요.",
		action: { label: "매장 규칙 보기", href: "/app/settings#rules" },
	},
	{
		id: "bulk",
		topic: "edit",
		q: "여러 건의 분류를 한꺼번에 바꿀 수 있나요?",
		a: "내역에서 '고르기'를 누르고 여러 건이나 '이 기간 전부'를 고른 뒤 분류 바꾸기 또는 휴지통으로 보낼 수 있어요.",
	},
	{
		id: "month-start",
		topic: "report",
		q: "월 시작일(월급날)을 바꾸고 싶어요.",
		a: "설정에서 월 시작일을 1~28일 중 골라요. 이번 달 합계, 리포트, 한도, 지난달 돌아보기가 모두 그 기준으로 바뀌어요.",
		action: { label: "월 시작일 바꾸기", href: "/app/settings#month-start" },
	},
	{
		id: "statement",
		topic: "report",
		q: "카드 명세서 합계와 기록이 달라요.",
		a: "리포트의 '카드 명세와 맞춰 보기'에 카드 앱에서 본 합계를 넣으면 차이와 기록 없는 날짜를 보여 줘요. 할부·해외 결제·취소 시점 때문에 다를 수 있어 참고용이에요.",
		action: { label: "리포트 열기", href: "/app/report" },
	},
	{
		id: "calendar",
		topic: "report",
		q: "달력으로 볼 수 있나요?",
		a: "내역 위 '달력' 버튼을 누르면 날짜별 합계, 정기 결제일, 공휴일이 함께 보여요. 날짜를 누르면 그날 기록이 나와요.",
	},
	{
		id: "tags",
		topic: "report",
		q: "여행·경조사처럼 따로 묶어 보고 싶어요.",
		a: "기록에 태그를 달면 리포트의 '태그별'에서 합계를 볼 수 있어요. 태그는 설정에서 만들고 이름을 바꾸거나 지울 수 있어요.",
	},
	{
		id: "reminder",
		topic: "report",
		q: "알림을 받을 수 있나요?",
		a: "웹에서는 정기 결제일과 월 마감·일요일 기록 알림을 휴대폰 달력에 넣는 방식으로 알려 드려요. 토스 앱에서는 토스 알림 동의로 받아요.",
		action: { label: "달력에 넣기", href: "/app/settings#reminders" },
	},
	{
		id: "restock",
		topic: "household",
		q: "다 쓴 물건은 어떻게 장보기에 넣나요?",
		a: "장보기 화면에서 물건의 '다 썼어'를 누르면 목록에 들어가요. 산 물건을 결제 기록에 연결하면 목록이 완료되고 재고가 다시 채워져요.",
		action: { label: "장보기 열기", href: "/app/household" },
	},
	{
		id: "forecast",
		topic: "household",
		q: "떨어질 날 예측은 언제부터 나오나요?",
		a: "같은 물건을 두 번 이상 산 기록이 있으면 평균 간격으로 계산해요. 7일 안으로 다가오면 홈 '이번 주 살 것'에 보여요.",
	},
	{
		id: "move",
		topic: "data",
		q: "다른 가계부나 엑셀 기록을 옮길 수 있나요?",
		a: "위플·편한가계부 CSV는 바로 읽고, 다른 표는 '다른 형식'에서 날짜·금액·내용 열을 골라 가져와요. 같은 기록은 두 번 들어가지 않아요.",
		action: { label: "옮기는 법", href: "/guide/move-from-other-ledger" },
	},
	{
		id: "backup",
		topic: "data",
		q: "휴대폰을 바꾸면 기록이 사라지나요?",
		a: "기록은 이 기기에만 있어요. 설정 → 복구코드 백업으로 암호화 파일을 만들고, 파일과 복구코드를 따로 보관하면 새 기기에서 복원할 수 있어요.",
		action: { label: "백업하러 가기", href: "/app/settings" },
	},
	{
		id: "delete",
		topic: "data",
		q: "기록을 전부 지우고 싶어요.",
		a: "설정 맨 아래 '전체 삭제'로 이 기기의 기록을 모두 지워요. 지우기 전에 CSV·JSON으로 내려받을 수 있어요.",
	},
	{
		id: "plus",
		topic: "plus",
		q: "돈을 내야 하는 기능이 있나요?",
		a: "기록·리포트·한도·달력·내보내기는 평생 무료예요. 깊은 회고와 인쇄용 결산만 한 번 구매하는 상품으로 준비 중이고, 지금은 구매할 수 없어요.",
		action: { label: "무료·유료 범위", href: "/pricing" },
	},
];

export function searchHelp(query: string): HelpItem[] {
	const terms = query
		.normalize("NFKC")
		.toLowerCase()
		.split(/\s+/)
		.filter(Boolean);
	if (!terms.length) return HELP_ITEMS;
	return HELP_ITEMS.filter((item) => {
		const text = `${item.q} ${item.a}`.toLowerCase().replace(/\s+/g, "");
		return terms.every((term) => text.includes(term));
	});
}
